define([
  'underscore',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'es6!launcher/util/listbuilder/wysiwyg/constants',
], (_, WysiwygUtil, WysiwygConstants) => {
  const {
    COMPONENT_SNAP_TOLERANCE,
    MINIMUM_COMPONENT_SIZE,
  } = WysiwygConstants;
  const {
    getRoundedPosition,
  } = WysiwygUtil;

  // Used to calculate snap position when moving a component.
  // In the case of components not having a height such as Text component, the height is zero
  // and the order of the if-else statements account for that. We order them from left to right
  // or top to bottom so if height or width is 0, an earlier if-else will be executed.
  function getMovingSnapPosition(draggingPosition, componentPosition) {
    const snappedPosition = _.clone(draggingPosition);
    const { left, top, width, height } = componentPosition;
    // Round down to find centers so we don't add a pixel by rounding up later.
    const hcenter = left + Math.floor(width / 2);
    const vcenter = top + Math.floor(height / 2);
    const right = left + width;
    const bottom = top + height;

    snappedPosition.hcenter = snappedPosition.left + snappedPosition.width / 2;
    snappedPosition.vcenter = snappedPosition.top + snappedPosition.height / 2;
    snappedPosition.right = snappedPosition.left + snappedPosition.width;
    snappedPosition.bottom = snappedPosition.top + snappedPosition.height;

    if (Math.abs(snappedPosition.left - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-left
      snappedPosition.left = left;
    } else if (Math.abs(snappedPosition.left - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-hcenter
      snappedPosition.left = hcenter;
    } else if (Math.abs(snappedPosition.left - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-right
      snappedPosition.left = right;
    } else if (Math.abs(snappedPosition.hcenter - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Hcenter-left
      snappedPosition.left = left - snappedPosition.width / 2;
    } else if (Math.abs(snappedPosition.hcenter - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Hcenter-hcenter
      snappedPosition.left = hcenter - snappedPosition.width / 2;
    } else if (Math.abs(snappedPosition.hcenter - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Hcenter-right
      snappedPosition.left = right - snappedPosition.width / 2;
    } else if (Math.abs(snappedPosition.right - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-left
      snappedPosition.left = left - snappedPosition.width;
    } else if (Math.abs(snappedPosition.right - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-hcenter
      snappedPosition.left = hcenter - snappedPosition.width;
    } else if (Math.abs(snappedPosition.right - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-right
      snappedPosition.left = right - snappedPosition.width;
    }

    if (Math.abs(snappedPosition.top - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-top
      snappedPosition.top = top;
    } else if (Math.abs(snappedPosition.top - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-vcenter
      snappedPosition.top = vcenter;
    } else if (Math.abs(snappedPosition.top - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-bottom
      snappedPosition.top = bottom;
    } else if (Math.abs(snappedPosition.vcenter - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Vcenter-top
      snappedPosition.top = top - snappedPosition.height / 2;
    } else if (Math.abs(snappedPosition.vcenter - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Vcenter-vcenter
      snappedPosition.top = vcenter - snappedPosition.height / 2;
    } else if (Math.abs(snappedPosition.vcenter - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Vcenter-bottom
      snappedPosition.top = bottom - snappedPosition.height / 2;
    } else if (Math.abs(snappedPosition.bottom - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-top
      snappedPosition.top = top - snappedPosition.height;
    } else if (Math.abs(snappedPosition.bottom - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-vcenter
      snappedPosition.top = vcenter - snappedPosition.height;
    } else if (Math.abs(snappedPosition.bottom - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-bottom
      snappedPosition.top = bottom - snappedPosition.height;
    }

    return getRoundedPosition(snappedPosition);
  }

  function getResizeLeftSnapPosition(resizingPosition, componentPosition) {
    const snappedPosition = _.clone(resizingPosition);
    const { left, width } = componentPosition;
    // Round down to find centers so we don't add a pixel by rounding up later.
    const hcenter = left + Math.floor(width / 2);
    const right = left + width;

    // Resizing left side.
    if (Math.abs(snappedPosition.left - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-left
      snappedPosition.width += snappedPosition.left - left;
      snappedPosition.left = left;
    } else if (Math.abs(snappedPosition.left - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-hcenter
      snappedPosition.width += snappedPosition.left - hcenter;
      snappedPosition.left = hcenter;
    } else if (Math.abs(snappedPosition.left - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-right
      snappedPosition.width += snappedPosition.left - right;
      snappedPosition.left = right;
    }

    if (snappedPosition.width < MINIMUM_COMPONENT_SIZE) return resizingPosition;
    return getRoundedPosition(snappedPosition);
  }

  function getResizeRightSnapPosition(resizingPosition, componentPosition) {
    const snappedPosition = _.clone(resizingPosition);
    const { left, width } = componentPosition;
    // Round down to find centers so we don't add a pixel by rounding up later.
    const hcenter = left + Math.floor(width / 2);
    const right = left + width;

    snappedPosition.right = snappedPosition.left + snappedPosition.width;

    // Resizing right side.
    if (Math.abs(snappedPosition.right - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-left
      snappedPosition.width = left - snappedPosition.left;
    } else if (Math.abs(snappedPosition.right - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-hcenter
      snappedPosition.width = hcenter - snappedPosition.left;
    } else if (Math.abs(snappedPosition.right - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-right
      snappedPosition.width = right - snappedPosition.left;
    }

    if (snappedPosition.width < MINIMUM_COMPONENT_SIZE) return resizingPosition;
    return getRoundedPosition(snappedPosition);
  }

  function getResizeTopSnapPosition(resizingPosition, componentPosition) {
    const snappedPosition = _.clone(resizingPosition);
    const { top, height } = componentPosition;
    // Round down to find centers so we don't add a pixel by rounding up later.
    const vcenter = top + Math.floor(height / 2);
    const bottom = top + height;

    // Resizing top side.
    if (Math.abs(snappedPosition.top - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-top
      snappedPosition.height += snappedPosition.top - top;
      snappedPosition.top = top;
    } else if (Math.abs(snappedPosition.top - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-vcenter
      snappedPosition.height += snappedPosition.top - vcenter;
      snappedPosition.top = vcenter;
    } else if (Math.abs(snappedPosition.top - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-bottom
      snappedPosition.height += snappedPosition.top - bottom;
      snappedPosition.top = bottom;
    }

    if (snappedPosition.height < MINIMUM_COMPONENT_SIZE) return resizingPosition;
    return getRoundedPosition(snappedPosition);
  }

  function getResizeBottomSnapPosition(resizingPosition, componentPosition) {
    const snappedPosition = _.clone(resizingPosition);
    const { top, height } = componentPosition;
    // Round down to find centers so we don't add a pixel by rounding up later.
    const vcenter = top + Math.floor(height / 2);
    const bottom = top + height;

    snappedPosition.bottom = snappedPosition.top + snappedPosition.height;

    // Resizing bottom side.
    if (Math.abs(snappedPosition.bottom - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-top
      snappedPosition.height = top - snappedPosition.top;
    } else if (Math.abs(snappedPosition.bottom - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-vcenter
      snappedPosition.height = vcenter - snappedPosition.top;
    } else if (Math.abs(snappedPosition.bottom - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-bottom
      snappedPosition.height = bottom - snappedPosition.top;
    }

    if (snappedPosition.height < MINIMUM_COMPONENT_SIZE) return resizingPosition;
    return getRoundedPosition(snappedPosition);
  }

  function _getLeftSideCornerResizingSnapPosition(resizingPosition, componentPosition, corner) {
    const snappedPosition = _.clone(resizingPosition);
    const aspectRatio = resizingPosition.aspectRatio;
    const { left, width } = componentPosition;
    const hcenter = left + Math.floor(width / 2);
    const right = left + width;

    if (!aspectRatio) {
      // More than likely doesn't need rounding but also picks out dimensions.
      return getRoundedPosition(resizingPosition);
    }

    // Resizing left side.
    if (Math.abs(snappedPosition.left - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-left
      snappedPosition.width += snappedPosition.left - left;
      snappedPosition.left = left;
      snappedPosition.height = snappedPosition.width * (1 / aspectRatio);
      // After snapping the left side and adjusting the height, if we are dragging top-left
      // corner, then we need to adjust the top as well. No height change will do nothing.
      if (corner === 'top-left') snappedPosition.top -= snappedPosition.height - resizingPosition.height;
    } else if (Math.abs(snappedPosition.left - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-hcenter
      snappedPosition.width += snappedPosition.left - hcenter;
      snappedPosition.left = hcenter;
      snappedPosition.height = snappedPosition.width * (1 / aspectRatio);
      // After snapping the left side and adjusting the height, if we are dragging top-left
      // corner, then we need to adjust the top as well. No height change will do nothing.
      if (corner === 'top-left') snappedPosition.top -= snappedPosition.height - resizingPosition.height;
    } else if (Math.abs(snappedPosition.left - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Left-right
      snappedPosition.width += snappedPosition.left - right;
      snappedPosition.left = right;
      snappedPosition.height = snappedPosition.width * (1 / aspectRatio);
      // After snapping the left side and adjusting the height, if we are dragging top-left
      // corner, then we need to adjust the top as well. No height change will do nothing.
      if (corner === 'top-left') snappedPosition.top -= snappedPosition.height - resizingPosition.height;
    }

    if (snappedPosition.width < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    if (snappedPosition.height < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    return getRoundedPosition(snappedPosition);
  }

  function _getRightSideCornerResizingSnapPosition(resizingPosition, componentPosition, corner) {
    const snappedPosition = _.clone(resizingPosition);
    const aspectRatio = resizingPosition.aspectRatio;
    const { left, width } = componentPosition;
    const hcenter = left + Math.floor(width / 2);
    const right = left + width;

    if (!aspectRatio) {
      // More than likely doesn't need rounding but also picks out dimensions.
      return getRoundedPosition(resizingPosition);
    }

    snappedPosition.right = snappedPosition.left + snappedPosition.width;

    // Resizing right side.
    if (Math.abs(snappedPosition.right - left) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-left
      snappedPosition.width = left - snappedPosition.left;
      snappedPosition.height = snappedPosition.width * (1 / aspectRatio);
      // After snapping the right side and adjusting the height, if we are dragging top-right
      // corner, then we need to adjust the top as well. No height change will do nothing.
      if (corner === 'top-right') snappedPosition.top -= snappedPosition.height - resizingPosition.height;
    } else if (Math.abs(snappedPosition.right - hcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-hcenter
      snappedPosition.width = hcenter - snappedPosition.left;
      snappedPosition.height = snappedPosition.width * (1 / aspectRatio);
      // After snapping the right side and adjusting the height, if we are dragging top-right
      // corner, then we need to adjust the top as well. No height change will do nothing.
      if (corner === 'top-right') snappedPosition.top -= snappedPosition.height - resizingPosition.height;
    } else if (Math.abs(snappedPosition.right - right) <= COMPONENT_SNAP_TOLERANCE) {
      // Right-right
      snappedPosition.width = right - snappedPosition.left;
      snappedPosition.height = snappedPosition.width * (1 / aspectRatio);
      // After snapping the right side and adjusting the height, if we are dragging top-right
      // corner, then we need to adjust the top as well. No height change will do nothing.
      if (corner === 'top-right') snappedPosition.top -= snappedPosition.height - resizingPosition.height;
    }

    if (snappedPosition.width < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    if (snappedPosition.height < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    return getRoundedPosition(snappedPosition);
  }

  function _getTopSideCornerResizingSnapPosition(resizingPosition, componentPosition, corner) {
    const snappedPosition = _.clone(resizingPosition);
    const aspectRatio = resizingPosition.aspectRatio;
    const { top, height } = componentPosition;
    const vcenter = top + Math.floor(height / 2);
    const bottom = top + height;

    if (!aspectRatio) {
      // More than likely doesn't need rounding but also picks out dimensions.
      return getRoundedPosition(resizingPosition);
    }

    snappedPosition.bottom = snappedPosition.top + snappedPosition.height;

    // Resizing top side.
    if (Math.abs(snappedPosition.top - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-top
      snappedPosition.height += snappedPosition.top - top;
      snappedPosition.top = top;
      snappedPosition.width = snappedPosition.height * aspectRatio;
      // After snapping the top side and adjusting the height, if we are dragging top-left
      // corner, then we need to adjust the left as well. No width change will do nothing.
      if (corner === 'top-left') snappedPosition.left -= snappedPosition.width - resizingPosition.width;
    } else if (Math.abs(snappedPosition.top - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-bottom
      snappedPosition.height += snappedPosition.top - vcenter;
      snappedPosition.top = vcenter;
      snappedPosition.width = snappedPosition.height * aspectRatio;
      // After snapping the top side and adjusting the height, if we are dragging top-left
      // corner, then we need to adjust the left as well. No width change will do nothing.
      if (corner === 'top-left') snappedPosition.left -= snappedPosition.width - resizingPosition.width;
    } else if (Math.abs(snappedPosition.top - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Top-bottom
      snappedPosition.height += snappedPosition.top - bottom;
      snappedPosition.top = bottom;
      snappedPosition.width = snappedPosition.height * aspectRatio;
      // After snapping the top side and adjusting the height, if we are dragging top-left
      // corner, then we need to adjust the left as well. No width change will do nothing.
      if (corner === 'top-left') snappedPosition.left -= snappedPosition.width - resizingPosition.width;
    }

    if (snappedPosition.width < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    if (snappedPosition.height < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    return getRoundedPosition(snappedPosition);
  }

  function _getBottomSideCornerResizingSnapPosition(resizingPosition, componentPosition, corner) {
    const snappedPosition = _.clone(resizingPosition);
    const aspectRatio = resizingPosition.aspectRatio;
    const { top, height } = componentPosition;
    const vcenter = top + Math.floor(height / 2);
    const bottom = top + height;

    if (!aspectRatio) {
      // More than likely doesn't need rounding but also picks out dimensions.
      return getRoundedPosition(resizingPosition);
    }

    snappedPosition.bottom = snappedPosition.top + snappedPosition.height;

    // Resizing bottom side.
    if (Math.abs(snappedPosition.bottom - top) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-top
      snappedPosition.height = top - snappedPosition.top;
      snappedPosition.width = snappedPosition.height * aspectRatio;
      // After snapping the bottom side and adjusting the width, if we are dragging bottom-left
      // corner, then we need to adjust the left as well. No width change will do nothing.
      if (corner === 'bottom-left') snappedPosition.left -= snappedPosition.width - resizingPosition.width;
    } else if (Math.abs(snappedPosition.bottom - vcenter) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-vcenter
      snappedPosition.height = vcenter - snappedPosition.top;
      snappedPosition.width = snappedPosition.height * aspectRatio;
      // After snapping the bottom side and adjusting the width, if we are dragging bottom-left
      // corner, then we need to adjust the left as well. No width change will do nothing.
      if (corner === 'bottom-left') snappedPosition.left -= snappedPosition.width - resizingPosition.width;
    } else if (Math.abs(snappedPosition.bottom - bottom) <= COMPONENT_SNAP_TOLERANCE) {
      // Bottom-bottom
      snappedPosition.height = bottom - snappedPosition.top;
      snappedPosition.width = snappedPosition.height * aspectRatio;
      // After snapping the bottom side and adjusting the width, if we are dragging bottom-left
      // corner, then we need to adjust the left as well. No width change will do nothing.
      if (corner === 'bottom-left') snappedPosition.left -= snappedPosition.width - resizingPosition.width;
    }

    if (snappedPosition.width < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    if (snappedPosition.height < MINIMUM_COMPONENT_SIZE) return getRoundedPosition(resizingPosition);
    return getRoundedPosition(snappedPosition);
  }

  function getResizeTopLeftSnapPosition(resizingPosition, componentPosition) {
    const CORNER = 'top-left';

    let snappedPosition = _getLeftSideCornerResizingSnapPosition(resizingPosition, componentPosition, CORNER);
    // Reset aspect ratio since these functions exclude it when returning.
    snappedPosition.aspectRatio = resizingPosition.aspectRatio;
    snappedPosition = _getTopSideCornerResizingSnapPosition(snappedPosition, componentPosition, CORNER);

    return snappedPosition;
  }

  function getResizeTopRightSnapPosition(resizingPosition, componentPosition) {
    const CORNER = 'top-right';

    let snappedPosition = _getRightSideCornerResizingSnapPosition(resizingPosition, componentPosition, CORNER);
    // Reset aspect ratio since these functions exclude it when returning.
    snappedPosition.aspectRatio = resizingPosition.aspectRatio;
    snappedPosition = _getTopSideCornerResizingSnapPosition(snappedPosition, componentPosition, CORNER);

    return snappedPosition;
  }

  function getResizeBottomLeftSnapPosition(resizingPosition, componentPosition) {
    const CORNER = 'bottom-left';

    let snappedPosition = _getLeftSideCornerResizingSnapPosition(resizingPosition, componentPosition, CORNER);
    // Reset aspect ratio since these functions exclude it when returning.
    snappedPosition.aspectRatio = resizingPosition.aspectRatio;
    snappedPosition = _getBottomSideCornerResizingSnapPosition(snappedPosition, componentPosition, CORNER);

    return snappedPosition;
  }

  function getResizeBottomRightSnapPosition(resizingPosition, componentPosition) {
    const CORNER = 'bottom-right';

    let snappedPosition = _getRightSideCornerResizingSnapPosition(resizingPosition, componentPosition, CORNER);
    // Reset aspect ratio since these functions exclude it when returning.
    snappedPosition.aspectRatio = resizingPosition.aspectRatio;
    snappedPosition = _getBottomSideCornerResizingSnapPosition(snappedPosition, componentPosition, CORNER);

    return snappedPosition;
  }

  function noSnapFunction (componentPosition) {
    return componentPosition;
  }

  const snapFunctionsByType = {
    move: getMovingSnapPosition,
    left: getResizeLeftSnapPosition,
    right: getResizeRightSnapPosition,
    top: getResizeTopSnapPosition,
    bottom: getResizeBottomSnapPosition,
    'top-left': getResizeTopLeftSnapPosition,
    'top-right': getResizeTopRightSnapPosition,
    'bottom-left': getResizeBottomLeftSnapPosition,
    'bottom-right': getResizeBottomRightSnapPosition,
  };

  function getSnapFunction(type) {
    return snapFunctionsByType[type];
  }

  return {
    getSnapFunction,
    noSnapFunction,
  };
});
